import React from 'react'
import moment from 'moment'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded'
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded'
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded'
import EditCalendarRoundedIcon from '@mui/icons-material/EditCalendarRounded'
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded'
import ViewTimelineRoundedIcon from '@mui/icons-material/ViewTimelineRounded'
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded'
import EnhancedEncryptionRoundedIcon from '@mui/icons-material/EnhancedEncryptionRounded'
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded'
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded'
import BuildRoundedIcon from '@mui/icons-material/BuildRounded'
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded'
import ChatRoundedIcon from '@mui/icons-material/ChatRounded'
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded'
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded'

// Shared between the header bell popover and the /notifications page, so the
// two can never render the same notification differently.

export const TYPE_ICONS = {
  shiftsAvailable: EventAvailableRoundedIcon,
  shiftAssigned: HowToRegRoundedIcon,
  shiftStartingSoon: EventAvailableRoundedIcon,
  shiftUpdated: EditCalendarRoundedIcon,
  shiftCancelled: EventBusyRoundedIcon,
  shiftApplicationOutcome: SendRoundedIcon,
  shiftCoverRequested: SwapHorizRoundedIcon,
  rotaPublished: ViewTimelineRoundedIcon,
  announcements: CampaignRoundedIcon,
  crewVaultItemAdded: EnhancedEncryptionRoundedIcon,
  appraisalDue: WorkspacePremiumRoundedIcon,
  ptsDuty: LocalShippingRoundedIcon,
  ptsPortalRequest: LocalShippingRoundedIcon,
  defectReported: BuildRoundedIcon,
  incidentReported: ReportProblemRoundedIcon,
  safeguardingConcernRaised: ReportProblemRoundedIcon,
  controlMessages: ChatRoundedIcon,
  clientActivity: HandshakeRoundedIcon,
  clientFeedbackReceived: RateReviewRoundedIcon,
}

// Notifications stored before the inbox kept a web link still go somewhere
// sensible when clicked.
export const FALLBACK_PATHS = {
  shiftsAvailable: '/available-shifts',
  shiftAssigned: '/my-shifts',
  shiftStartingSoon: '/my-shifts',
  shiftUpdated: '/my-shifts',
  shiftCancelled: '/my-shifts',
  shiftApplicationOutcome: '/my-shifts',
  rotaPublished: '/my-shifts',
  defectReported: '/defects',
}

export const getWebPath = (notification) =>
  notification.webUrl ?? FALLBACK_PATHS[notification.type]

export const NotificationRow = ({ notification, onClick }) => {
  const theme = useTheme()
  const unread = !notification.readAt
  const Icon = TYPE_ICONS[notification.type] ?? NotificationsNoneRoundedIcon
  const clickable = unread || !!getWebPath(notification)

  return (
    <Stack
      component="li"
      direction="row"
      gap={1.25}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={() => onClick(notification)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick(notification)
        }
      }}
      sx={{
        listStyle: 'none',
        px: 2,
        py: 1.25,
        cursor: clickable ? 'pointer' : 'default',
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: unread
          ? alpha(theme.palette.anBlue.main, 0.05)
          : 'transparent',
        '&:last-of-type': { borderBottom: 0 },
        '&:hover': clickable ? { bgcolor: 'action.hover' } : {},
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: 1,
          display: 'grid',
          placeItems: 'center',
          bgcolor: alpha(
            unread ? theme.palette.anBlue.main : theme.palette.text.secondary,
            0.1,
          ),
          color: unread ? 'anBlue.main' : 'text.secondary',
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Stack flex={1} minWidth={0} gap={0.25}>
        <Typography
          variant="body2"
          fontWeight={unread ? 800 : 600}
          color={unread ? 'text.primary' : 'text.secondary'}
          noWrap
        >
          {notification.title}
        </Typography>
        {notification.body && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {notification.body}
          </Typography>
        )}
        <Typography variant="caption" color="text.disabled">
          {moment(notification.createdAt).fromNow()}
        </Typography>
      </Stack>
      {unread && (
        <Box
          sx={{
            width: 8,
            height: 8,
            mt: 0.75,
            flexShrink: 0,
            borderRadius: '50%',
            bgcolor: 'anBlue.main',
          }}
        />
      )}
    </Stack>
  )
}
